<template>
  <section class="testimonial-section">
    <div class="section-title" data-aos="fade-down" data-aos-duration="500">
      <h2 class="main-title">اراء عملائنا</h2>
    </div>

    <div v-if="content.loading && content.clientOpinions.length === 0" class="testimonial-state">
      جارِ تحميل آراء العملاء...
    </div>
    <div v-else-if="content.error && content.clientOpinions.length === 0" class="testimonial-state">
      تعذر تحميل آراء العملاء حاليًا.
    </div>
    <div v-else-if="content.clientOpinionsLoaded && content.clientOpinions.length === 0" class="testimonial-state">
      لا توجد آراء عملاء منشورة حاليًا.
    </div>
    <template v-else>
      <div class="testimonial-content desktop-testimonial">
        <div class="container">
          <div class="row">
            <div v-for="item in desktopOpinions" :key="item.clientOpinionId" class="col-lg-4">
              <div
                class="testimonial-card"
                data-aos="fade-up"
                data-aos-delay="150"
                data-aos-duration="500"
              >
                <img :src="item.image || fallbackImage" :alt="item.clientName" />
                <p class="test-description">{{ item.opinion }}</p>
                <div class="testimonial-info">
                  <h5 class="testimonial-name">{{ item.clientName }}</h5>
                  <ul class="testimonial-rates">
                    <li v-for="star in ratingStars(item.rating)" :key="star">
                      <i class="fa fa-star"></i>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="testimonial-mobile">
        <div class="swiper testimonial-wrapper">
          <div class="swiper-wrapper">
            <div
              v-for="item in content.clientOpinions"
              :key="item.clientOpinionId"
              class="swiper-slide"
            >
              <div
                class="testimonial-card"
                data-aos="fade-up"
                data-aos-delay="250"
                data-aos-duration="500"
              >
                <img :src="item.image || fallbackImage" :alt="item.clientName" />
                <p class="test-description">{{ item.opinion }}</p>
                <div class="testimonial-info">
                  <h5 class="testimonial-name">{{ item.clientName }}</h5>
                  <ul class="testimonial-rates">
                    <li v-for="star in ratingStars(item.rating)" :key="star">
                      <i class="fa fa-star"></i>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          <div class="swiper-button-next testimonial-next">
            <i class="fas fa-chevron-left"></i>
          </div>
          <div class="swiper-button-prev testimonial-prev">
            <i class="fas fa-chevron-right"></i>
          </div>
        </div>
      </div>
    </template>
  </section>
</template>

<script lang="ts" setup>
// Was fully static: three hardcoded review cards, all three attributed to
// the same fictional reviewer name ("مشعل محمد") with identical text - see
// docs/v4-migration/BROKEN-FEATURES-FOUND.md. Now backed by the real
// GET WebsiteContent/ClientOpinions endpoint (API Integrations v4 only -
// this content source did not exist in v2).
//
// NOTE on `item.image`: the backend stores exactly what the admin/CMS write
// path supplies for this field (Helper/Core/WebSite/ClientOpinionsHelper.cs
// - no server-side URL construction happens), unlike the worker photo path
// used elsewhere in this app, which is a hardcoded
// "https://office.musa3ed.com/up/{companyId}/Applicant/{file}" convention
// confirmed already live in production. There was no equivalent confirmed
// convention to reuse here, so this component renders `item.image` as-is
// (assumed to already be a complete, directly usable URL) rather than
// inventing an unverified prefix. Recorded as an open verification item in
// docs/v4-migration/DERAYAH-VUE-V4-FULL-MODERNIZATION-CERT-001.md - confirm
// against real published content once available.
import { computed, onMounted } from "vue";
import { useWebsiteContentStore } from "@/stores/websiteContent";

const content = useWebsiteContentStore();
const fallbackImage = "/assets/images/backgrounds/test-img.jpeg";

const desktopOpinions = computed(() => content.clientOpinions.slice(0, 3));

function ratingStars(rating: number): number[] {
  const clamped = Math.min(5, Math.max(0, rating));
  return Array.from({ length: clamped }, (_, index) => index);
}

onMounted(() => {
  if (!content.clientOpinionsLoaded) {
    content.getClientOpinions();
  }
});
</script>

<style scoped>
.testimonial-state {
  text-align: center;
  padding: 1rem 0 2rem;
}
</style>
